import { PixiApp } from './app.js';
import { Engine } from '@nebulark/engine';
import { STAGE1, STAGE2, WEAPONS, ENEMIES, PICKUPS, BOSS_STAGE1 } from '@nebulark/content';
import { Renderer } from './render/renderer.js';
import { TouchInput } from './input/touch.js';
import { GameLoop } from './loop.js';
import { TitleScreen } from './screens/title.js';
import { GameOverScreen } from './screens/gameover.js';
import { playSfx } from './audio/sfx.js';
import { loadHiScore, saveHiScore } from './storage/highscore.js';
import type { EngineEvent, Snapshot, LevelScript } from '@nebulark/engine';

const WORLD_W = 360;
const WORLD_H = 640;

type AppPhase = 'title' | 'playing' | 'gameover' | 'cleared';

async function main(): Promise<void> {
  const pixiApp = new PixiApp();
  await pixiApp.init();

  const stage = pixiApp.app.stage;

  function makeEngine(level: LevelScript = STAGE1): Engine {
    return new Engine({
      seed: Math.floor(Math.random() * 0xffffffff),
      playerCount: 1,
      worldWidth: WORLD_W,
      worldHeight: WORLD_H,
      fixedDtMs: 1000 / 60,
      level,
      weapons: WEAPONS,
      enemies: ENEMIES,
      pickups: PICKUPS,
      bosses: { 'boss-stage1': BOSS_STAGE1 },
    });
  }

  let engine = makeEngine();
  const renderer = new Renderer();
  let stageIndex = 0;
  const STAGES: LevelScript[] = [STAGE1, STAGE2];

  const touch = new TouchInput(
    pixiApp.app.canvas as HTMLCanvasElement,
    (clientX, clientY) => {
      // HUD hitbox is in world coords; convert to client coords for comparison.
      const s = pixiApp.getScreenInfo();
      const hb = renderer.hud.getBombButtonHitbox();
      const hbClientX = hb.x * s.scale + s.offsetX;
      const hbClientY = hb.y * s.scale + s.offsetY;
      const hbClientR = hb.radius * s.scale;
      const dx = clientX - hbClientX;
      const dy = clientY - hbClientY;
      return dx * dx + dy * dy <= hbClientR * hbClientR;
    },
  );

  const title = new TitleScreen();
  const gameover = new GameOverScreen();

  // Wire events from renderer → sfx
  renderer.onEvent((e: EngineEvent) => {
    switch (e.type) {
      case 'shoot':   playSfx(`shoot_${e.weapon}`); break;
      case 'hit':     playSfx(e.heavy ? 'hit_player' : 'hit_enemy'); break;
      case 'explode': playSfx(e.magnitude > 5 ? 'explode_big' : 'explode_small'); break;
      case 'pickup':  playSfx('pickup'); break;
      case 'bomb':    playSfx('bomb'); break;
      case 'playerDeath': playSfx('hit_player'); break;
      case 'bossPhase':   playSfx('boss_phase'); break;
      case 'bossDown':    playSfx('explode_big'); break;
      case 'gameover':    playSfx('gameover'); break;
    }
  });

  let phase: AppPhase = 'title';
  let loop: GameLoop | null = null;

  function showTitle(): void {
    phase = 'title';
    loop?.stop();
    stage.removeChildren();
    stage.addChild(title.container);
  }

  function startGame(carriedScore = 0, carriedLives = 3, nextStageIndex = 0): void {
    loop?.stop();
    stageIndex = nextStageIndex;
    phase = 'playing';
    const level = STAGES[stageIndex % STAGES.length]!;
    engine = makeEngine(level);

    // Carry score and lives from previous stage
    if (carriedScore > 0 || carriedLives !== 3) {
      const s = engine.serialize() as Record<string, unknown>;
      const players = s['players'] as Array<Record<string, unknown>> | undefined;
      if (players && players[0]) {
        players[0]['score'] = carriedScore;
        players[0]['lives'] = Math.max(1, carriedLives);
      }
      engine.deserialize(s);
    }

    const hiScore = loadHiScore();
    stage.removeChildren();
    stage.addChild(renderer.root);
    gameover.container.visible = false;

    loop = new GameLoop(engine, touch, (snap: Snapshot, events: EngineEvent[], dtMs: number) => {
      renderer.render(snap, dtMs);
      renderer.emit(events);

      if (snap.phase === 'gameover' && phase === 'playing') {
        phase = 'gameover';
        const finalScore = snap.players[0]?.score ?? 0;
        const hi = Math.max(finalScore, hiScore);
        saveHiScore(hi);
        setTimeout(() => {
          stage.addChild(gameover.container);
          gameover.show(finalScore, hi);
        }, 1200);
      }

      if (snap.phase === 'cleared' && phase === 'playing') {
        phase = 'cleared';
        const carriedSc = snap.players[0]?.score ?? 0;
        const carriedLv = snap.players[0]?.lives ?? 1;
        loop?.stop();
        // Show brief stage-clear banner via DOM
        const banner = document.createElement('div');
        banner.textContent = `STAGE ${stageIndex + 1} CLEAR!`;
        Object.assign(banner.style, {
          position: 'fixed', inset: '0', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: '48px', fontWeight: 'bold',
          color: '#44ffbb', fontFamily: 'monospace', pointerEvents: 'none',
          textShadow: '0 0 20px #44ffbb',
        });
        document.body.appendChild(banner);
        setTimeout(() => {
          document.body.removeChild(banner);
          startGame(carriedSc, carriedLv, stageIndex + 1);
        }, 2000);
      }
    });
    loop.start();
  }

  title.onStartGame(startGame);
  gameover.onRestartGame(() => {
    startGame(0, 3, 0);
  });

  // Ticker for title blink
  pixiApp.app.ticker.add((ticker) => {
    const dt = ticker.deltaMS;
    if (phase === 'title') title.update(dt);
    if (phase === 'gameover') gameover.update(dt);
  });

  showTitle();
}

main().catch(console.error);
